import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category colors and icons
const CATEGORY_CONFIG = {
  toys: { color: '#f472b6', icon: '🧸', label: 'Toys' },
  food: { color: '#fb923c', icon: '🍕', label: 'Food' },
  games: { color: '#a78bfa', icon: '🎮', label: 'Games' },
  clothes: { color: '#60a5fa', icon: '👕', label: 'Clothes' },
  entertainment: { color: '#34d399', icon: '🎬', label: 'Entertainment' },
  savings: { color: '#fbbf24', icon: '💰', label: 'Savings' },
  giving: { color: '#ec4899', icon: '💝', label: 'Giving' },
  other: { color: '#94a3b8', icon: '📦', label: 'Other' }
};

/**
 * Custom tooltip for pie chart
 */
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const config = CATEGORY_CONFIG[data.category] || CATEGORY_CONFIG.other;
    return (
      <div className="bg-white shadow-lg rounded-lg p-3 border">
        <p className="font-medium flex items-center gap-2">
          <span>{config.icon}</span>
          {config.label}
        </p>
        <p className="text-slate-600">${data.total.toFixed(2)}</p>
        <p className="text-xs text-slate-400">{data.count} transaction(s)</p>
      </div>
    );
  }
  return null;
};

/**
 * Custom legend
 */
const CustomLegend = ({ payload }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2 mt-2">
      {payload.map((entry, index) => {
        const config = CATEGORY_CONFIG[entry.payload.category] || CATEGORY_CONFIG.other;
        return (
          <div 
            key={index}
            className="flex items-center gap-1 text-xs bg-slate-50 px-2 py-1 rounded-full"
          >
            <span>{config.icon}</span>
            <span className="text-slate-600">{config.label}</span>
            <span className="font-medium">{entry.payload.percentage}%</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Spending Categories Chart Component
 */
export const SpendingCategories = ({ className }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/transactions/by-category`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = data.map(item => ({
    ...item,
    name: CATEGORY_CONFIG[item.category]?.label || item.category,
    fill: CATEGORY_CONFIG[item.category]?.color || '#94a3b8'
  }));

  const totalSpent = data.reduce((sum, item) => sum + item.total, 0);

  return (
    <Card className={cn("shadow-lg rounded-2xl", className)} data-testid="spending-categories-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          📊 Where My Money Goes
        </CardTitle>
        <CardDescription>
          {data.length > 0 
            ? `Total spent: $${totalSpent.toFixed(2)}` 
            : 'Track your spending by category'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-4xl mb-2">📊</p>
            <p className="text-slate-500">No spending data yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Make some withdrawals with categories to see your spending breakdown!
            </p>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="45%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="total"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Export category options for transaction form
export const SPENDING_CATEGORIES = Object.entries(CATEGORY_CONFIG).map(([key, value]) => ({
  value: key,
  label: value.label,
  icon: value.icon
}));

export default SpendingCategories;
